import api from './api';
import { API_URL } from '../config';

// Request cache to prevent duplicate calls
const requestCache = new Map<string, Promise<any>>();

interface AuthResponse {
  access_token: string;
}

interface UserInfo {
  id: number;
  email: string;
}

export const authService = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (email: string, password: string, firstName: string, lastName: string): Promise<AuthResponse> => {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        firstName,
        lastName
      });
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    try {
      await api.post(`/auth/reset-password/${token}`, { newPassword });
    } catch (error) {
      throw error;
    }
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      // Get the authentication token from localStorage
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      
      // Use the change-password endpoint with proper headers
      await api.post('/users/change-password', { 
        currentPassword,
        newPassword 
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
    } catch (error) {
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getCurrentUser: async (): Promise<UserInfo> => {
    const cacheKey = 'getCurrentUser';
    
    // Check if there's already a pending request
    if (requestCache.has(cacheKey)) {
      return requestCache.get(cacheKey)!;
    }
    
    // Create new request
    const request = (async () => {
      try {
        const response = await api.get('/users/profile');
        return response.data;
      } catch (error) {
        // Remove from cache on error so we can retry
        requestCache.delete(cacheKey);
        throw error;
      }
    })();
    
    // Cache the request
    requestCache.set(cacheKey, request);
    
    // Clean up cache after request completes (success or failure)
    request.finally(() => {
      requestCache.delete(cacheKey);
    });
    
    return request;
  },

  // Google OAuth functions
  initiateGoogleAuth: () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${API_URL}/auth/google`;
  },

  handleGoogleCallback: async (): Promise<AuthResponse> => {
    try {
      // This will be called after the callback redirect
      const response = await api.get('/auth/google/callback');
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
